import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

export const AdminDashboard = () => {
  // Auth state - NO PASSWORD STORED HERE
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [authToken, setAuthToken] = useState('');
  
  // Dashboard data
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterTier, setFilterTier] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const savedAuth = sessionStorage.getItem('da_admin_auth');
    const savedToken = sessionStorage.getItem('da_admin_token');
    if (savedAuth === 'true' && savedToken) {
      setIsAdminAuthenticated(true);
      setAuthToken(savedToken);
    }
  }, []);

  // Fetch dashboard data when authenticated
  useEffect(() => {
    if (isAdminAuthenticated && authToken) {
      fetchDashboardData();
    }
  }, [isAdminAuthenticated, authToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      // Validate password with backend - SECURE
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });

      if (!response.ok) {
        throw new Error('Invalid password');
      }

      const data = await response.json();
      
      if (data.success) {
        // Store auth state and token securely
        // Create Basic auth token from password (empty username + password)
        const basicAuthToken = btoa(':' + password);
        setIsAdminAuthenticated(true);
        setAuthToken(basicAuthToken);
        sessionStorage.setItem('da_admin_auth', 'true');
        sessionStorage.setItem('da_admin_token', basicAuthToken);
        setPassword(''); // Clear password input immediately
      }
    } catch (err) {
      setLoginError('Incorrect password. Please try again.');
      setPassword(''); // Clear password input on error
    }
  };

  const fetchDashboardData = async () => {
    if (!isAdminAuthenticated || !authToken) return; // Only fetch when authenticated
    
    setLoading(true);
    try {
      const auth = authToken.startsWith('Basic ') ? authToken : 'Basic ' + authToken;
      
      const appsResponse = await fetch(`${BACKEND_URL}/api/admin/applications`, {
        headers: { 'Authorization': auth }
      });
      
      if (appsResponse.ok) {
        const appsData = await appsResponse.json();
        setApplications(appsData);
      } else if (appsResponse.status === 401) {
        // Session expired - force logout
        handleLogout();
      }

      const statsResponse = await fetch(`${BACKEND_URL}/api/admin/stats`, {
        headers: { 'Authorization': auth }
      });
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!authToken) return;
    
    try {
      const auth = authToken.startsWith('Basic ') ? authToken : 'Basic ' + authToken;
      const response = await fetch(`${BACKEND_URL}/api/admin/export`, {
        headers: { 'Authorization': auth }
      });
      
      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DA_Applications_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Failed to export: ' + err.message);
    }
  };

  const handleDownloadScoringGuide = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/scoring-guide`);
      
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'DA_Scoring_Guide.html';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert('Failed to download scoring guide: ' + err.message);
    }
  };

  const handleLogout = () => {
    // Clear all auth state and data
    setIsAdminAuthenticated(false);
    setAuthToken('');
    setPassword('');
    setApplications([]);
    setStats(null);
    setSelectedApplicant(null);
    
    // Clear secure session storage (not localStorage)
    sessionStorage.removeItem('da_admin_auth');
    sessionStorage.removeItem('da_admin_token');
  };

  const filteredApplications = applications.filter(app => {
    const matchesTier = filterTier === 'all' || app.tier === filterTier;
    const matchesSearch = !searchTerm || 
      app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTier && matchesSearch;
  });

  const handleViewDetails = (app) => {
    setSelectedApplicant(app);
    setShowDetail(true);
  };

  const handleCloseDetail = () => {
    setShowDetail(false);
    setSelectedApplicant(null);
  };

  const handleDeleteApplication = async (appId, appName) => {
    // Confirm before deleting
    const confirmed = window.confirm(`Are you sure you want to delete the application from "${appName}"? This action cannot be undone.`);
    
    if (!confirmed) return;
    
    try {
      const auth = authToken.startsWith('Basic ') ? authToken : 'Basic ' + authToken;
      const response = await fetch(`${BACKEND_URL}/api/admin/applications/${appId}`, {
        method: 'DELETE',
        headers: { 'Authorization': auth }
      });
      
      if (!response.ok) throw new Error('Delete failed');
      
      // Remove from local state
      setApplications(applications.filter(app => app.id !== appId));
      
      // Close detail modal if this app was selected
      if (selectedApplicant && selectedApplicant.id === appId) {
        handleCloseDetail();
      }
      
      // Refresh dashboard data to update stats
      fetchDashboardData();
      
      alert('Application deleted successfully');
    } catch (err) {
      alert('Failed to delete application: ' + err.message);
    }
  };

  const styles = {
    container: { minHeight: '100vh', background: '#0b0b0c', color: 'white', padding: '2rem' },
    loginBox: { maxWidth: '400px', margin: '10rem auto', background: '#1a1a1c', padding: '2rem', borderRadius: '16px', border: '2px solid #c8a43a' },
    input: { width: '100%', padding: '0.8rem', marginBottom: '1rem', border: '1px solid #333', borderRadius: '8px', background: '#2a2a2c', color: 'white' },
    btn: { width: '100%', background: '#c8a43a', color: '#0b0b0c', padding: '0.8rem', border: 0, borderRadius: '8px', fontWeight: 700, cursor: 'pointer', fontSize: '1rem' },
    btnSecondary: { background: '#e5e7eb', color: '#111827', padding: '0.6rem 1rem', border: 0, borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem', marginLeft: '0.5rem' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', borderBottom: '2px solid #c8a43a', paddingBottom: '1rem' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' },
    statCard: { background: '#1a1a1c', padding: '1.5rem', borderRadius: '12px', border: '1px solid #333' },
    table: { width: '100%', background: '#1a1a1c', borderRadius: '12px', overflow: 'hidden' },
    th: { padding: '1rem', textAlign: 'left', borderBottom: '2px solid #c8a43a', background: '#0b0b0c' },
    td: { padding: '1rem', borderBottom: '1px solid #333', cursor: 'pointer' },
    tdHover: { padding: '1rem', borderBottom: '1px solid #333', cursor: 'pointer', background: '#2a2a2c' },
    filter: { padding: '0.6rem', border: '1px solid #333', borderRadius: '8px', background: '#2a2a2c', color: 'white', marginRight: '0.5rem' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', overflow: 'auto' },
    modalContent: { background: '#1a1a1c', borderRadius: '16px', border: '2px solid #c8a43a', maxWidth: '900px', width: '100%', maxHeight: '90vh', overflow: 'auto', padding: '2rem' },
    detailSection: { marginBottom: '1.5rem', padding: '1rem', background: '#2a2a2c', borderRadius: '8px' },
    detailLabel: { color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.25rem' },
    detailValue: { color: 'white', fontSize: '1rem', fontWeight: 500 }
  };

  // Login Screen - Only show when NOT authenticated
  if (!isAdminAuthenticated) {
    return (
      <div style={styles.container}>
        <div style={styles.loginBox}>
          <h1 style={{ fontSize: '2rem', marginBottom: '1.5rem', color: '#c8a43a', textAlign: 'center' }}>
            Admin Dashboard
          </h1>
          <form onSubmit={handleLogin}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#9ca3af', fontWeight: 500 }}>
              Admin Password
            </label>
            <input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              required
              autoFocus
            />
            {loginError && (
              <p style={{ 
                color: '#ef4444', 
                marginBottom: '1rem', 
                marginTop: '0.5rem',
                fontSize: '0.9rem',
                textAlign: 'center'
              }}>
                {loginError}
              </p>
            )}
            <button type="submit" style={styles.btn}>Log In</button>
          </form>
        </div>
      </div>
    );
  }

  // Admin Dashboard - Only show when authenticated

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={{ fontSize: '2rem', color: '#c8a43a', margin: 0 }}>
          DA Membership Dashboard
        </h1>
        <div>
          <button onClick={handleDownloadScoringGuide} style={styles.btnSecondary}>
            📄 Download Scoring Guide
          </button>
          <button onClick={handleExport} style={styles.btnSecondary}>
            📊 Export to Excel
          </button>
          <button onClick={handleLogout} style={styles.btnSecondary}>
            Logout
          </button>
        </div>
      </div>

      {loading ? (
        <p>Loading dashboard...</p>
      ) : (
        <>
          {/* Stats Cards */}
          {stats && (
            <div style={styles.statsGrid}>
              <div style={styles.statCard}>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#9ca3af' }}>Total Applications</h3>
                <p style={{ fontSize: '2.5rem', fontWeight: 700, margin: 0, color: '#c8a43a' }}>
                  {stats.total_submissions}
                </p>
              </div>
              <div style={styles.statCard}>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#9ca3af' }}>Gold Tier</h3>
                <p style={{ fontSize: '2.5rem', fontWeight: 700, margin: 0, color: '#fbbf24' }}>
                  {stats.gold_count}
                </p>
              </div>
              <div style={styles.statCard}>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#9ca3af' }}>Silver Tier</h3>
                <p style={{ fontSize: '2.5rem', fontWeight: 700, margin: 0, color: '#d1d5db' }}>
                  {stats.silver_count}
                </p>
              </div>
              <div style={styles.statCard}>
                <h3 style={{ margin: '0 0 0.5rem 0', color: '#9ca3af' }}>Audition Offers</h3>
                <p style={{ fontSize: '2.5rem', fontWeight: 700, margin: 0, color: '#10b981' }}>
                  {stats.audition_offers}
                </p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div style={{ marginBottom: '1.5rem' }}>
            <select value={filterTier} onChange={(e) => setFilterTier(e.target.value)} style={styles.filter}>
              <option value="all">All Tiers</option>
              <option value="Gold">Gold</option>
              <option value="Silver">Silver</option>
              <option value="Bronze">Bronze</option>
              <option value="Limited">Limited</option>
              <option value="Review">Review</option>
            </select>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ ...styles.filter, width: '300px' }}
            />
          </div>

          {/* Applications Table */}
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Tier</th>
                  <th style={styles.th}>Total Score</th>
                  <th style={styles.th}>Audition</th>
                  <th style={styles.th}>Submitted</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ ...styles.td, textAlign: 'center', padding: '2rem' }}>
                      No applications found
                    </td>
                  </tr>
                ) : (
                  filteredApplications.map((app, idx) => (
                    <tr key={app.id || idx} onMouseEnter={(e) => e.currentTarget.style.background = '#2a2a2c'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                      <td style={styles.td} onClick={() => handleViewDetails(app)} style={{ cursor: 'pointer' }}>{app.name}</td>
                      <td style={styles.td} onClick={() => handleViewDetails(app)} style={{ cursor: 'pointer' }}>{app.email}</td>
                      <td style={styles.td} onClick={() => handleViewDetails(app)} style={{ cursor: 'pointer' }}>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '999px',
                          fontSize: '0.85rem',
                          fontWeight: 700,
                          background: app.tier === 'Gold' ? '#fbbf24' : app.tier === 'Silver' ? '#d1d5db' : '#9ca3af',
                          color: '#0b0b0c'
                        }}>
                          {app.tier}
                        </span>
                      </td>
                      <td style={styles.td} onClick={() => handleViewDetails(app)} style={{ cursor: 'pointer' }}>{app.total_score}/60</td>
                      <td style={styles.td} onClick={() => handleViewDetails(app)} style={{ cursor: 'pointer' }}>{app.audition_offer === 'yes' ? '✅' : '—'}</td>
                      <td style={styles.td} onClick={() => handleViewDetails(app)} style={{ cursor: 'pointer' }}>
                        {new Date(app.created_at).toLocaleDateString()}
                      </td>
                      <td style={styles.td}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteApplication(app.id, app.name);
                          }}
                          style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            padding: '0.4rem 0.8rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: 600
                          }}
                          onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                          onMouseLeave={(e) => e.target.style.background = '#ef4444'}
                        >
                          🗑️ Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Detail Modal */}
      {showDetail && selectedApplicant && (
        <div style={styles.modal} onClick={handleCloseDetail}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', borderBottom: '2px solid #c8a43a', paddingBottom: '1rem' }}>
              <h2 style={{ margin: 0, color: '#c8a43a', fontSize: '1.8rem' }}>Applicant Details</h2>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  onClick={() => {
                    handleDeleteApplication(selectedApplicant.id, selectedApplicant.name);
                  }}
                  style={{
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 600
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#dc2626'}
                  onMouseLeave={(e) => e.target.style.background = '#ef4444'}
                >
                  🗑️ Delete Application
                </button>
                <button onClick={handleCloseDetail} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '2rem', cursor: 'pointer', padding: '0 0.5rem' }}>×</button>
              </div>
            </div>

            {/* Personal Information */}
            <div style={styles.detailSection}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#c8a43a' }}>Personal Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <div style={styles.detailLabel}>Full Name</div>
                  <div style={styles.detailValue}>{selectedApplicant.name}</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Email</div>
                  <div style={styles.detailValue}>{selectedApplicant.email}</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Phone Number</div>
                  <div style={styles.detailValue}>{selectedApplicant.phone || 'Not provided'}</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Vocal Part</div>
                  <div style={styles.detailValue}>{selectedApplicant.part || 'Not specified'}</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Is Soloist?</div>
                  <div style={styles.detailValue}>{selectedApplicant.is_soloist || 'Not answered'}</div>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <div style={styles.detailLabel}>Motivation</div>
                  <div style={styles.detailValue}>{selectedApplicant.motivation || 'Not provided'}</div>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <div style={styles.detailLabel}>Personality</div>
                  <div style={styles.detailValue}>{selectedApplicant.personality || 'Not provided'}</div>
                </div>
              </div>
            </div>

            {/* Commitment & Availability */}
            <div style={styles.detailSection}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#c8a43a' }}>Commitment & Availability</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <div style={styles.detailLabel}>Rehearsal Attendance</div>
                  <div style={styles.detailValue}>{selectedApplicant.rehearsal}/4 points</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Travel Willingness</div>
                  <div style={styles.detailValue}>{selectedApplicant.travel}/4 points</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Planning Ability</div>
                  <div style={styles.detailValue}>{selectedApplicant.planning}/4 points</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Reliable Transportation to Baltimore</div>
                  <div style={styles.detailValue}>{selectedApplicant.reliable_transportation || 'Not answered'}</div>
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <div style={styles.detailLabel}>Agrees to Be Communicative</div>
                  <div style={styles.detailValue}>{selectedApplicant.communicative || 'Not answered'}</div>
                </div>
              </div>
            </div>

            {/* Financial Responsibility */}
            <div style={styles.detailSection}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#c8a43a' }}>Financial Responsibility</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <div style={styles.detailLabel}>Can Contribute to Travel/Attire</div>
                  <div style={styles.detailValue}>{selectedApplicant.finance_travel}/4 points</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Understands Choir Funds</div>
                  <div style={styles.detailValue}>{selectedApplicant.finance_understand}/4 points</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Monthly $20 Donation</div>
                  <div style={styles.detailValue}>{selectedApplicant.monthly_donation || 'Not answered'}</div>
                </div>
              </div>
            </div>

            {/* Singing Discipline */}
            <div style={styles.detailSection}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#c8a43a' }}>Singing Discipline</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <div style={styles.detailLabel}>Practice Frequency</div>
                  <div style={styles.detailValue}>{selectedApplicant.practice}/4 points</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Handles Musical Direction</div>
                  <div style={styles.detailValue}>{selectedApplicant.direction}/4 points</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Confidence Level</div>
                  <div style={styles.detailValue}>{selectedApplicant.confidence}/5</div>
                </div>
              </div>
            </div>

            {/* Alignment with DA Vision */}
            <div style={styles.detailSection}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#c8a43a' }}>Alignment with DA Vision</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <div style={styles.detailLabel}>Ministry Excellence</div>
                  <div style={styles.detailValue}>{selectedApplicant.score_vision}/4 points</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Unity & Professionalism</div>
                  <div style={styles.detailValue}>{selectedApplicant.score_unity}/4 points</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Conflict Management</div>
                  <div style={styles.detailValue}>{selectedApplicant.score_conflict}/4 points</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Why Divine Anointing</div>
                  <div style={styles.detailValue}>{selectedApplicant.score_why}/4 points</div>
                </div>
              </div>
            </div>

            {/* Final Scores */}
            <div style={styles.detailSection}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#c8a43a' }}>Assessment Results</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                <div>
                  <div style={styles.detailLabel}>Commitment Score</div>
                  <div style={styles.detailValue}>{selectedApplicant.score_commitment}/12</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Finance Score</div>
                  <div style={styles.detailValue}>{selectedApplicant.score_finance}/8</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Singing Score</div>
                  <div style={styles.detailValue}>{selectedApplicant.score_singing}/12</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Alignment Score</div>
                  <div style={styles.detailValue}>{selectedApplicant.score_alignment}/16</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Total Score</div>
                  <div style={{ ...styles.detailValue, fontSize: '1.5rem', color: '#c8a43a' }}>{selectedApplicant.total_score}/60</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Tier</div>
                  <div style={{ ...styles.detailValue, fontSize: '1.2rem' }}>
                    <span style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '999px',
                      fontWeight: 700,
                      background: selectedApplicant.tier === 'Gold' ? '#fbbf24' : selectedApplicant.tier === 'Silver' ? '#d1d5db' : '#9ca3af',
                      color: '#0b0b0c'
                    }}>
                      {selectedApplicant.tier}
                    </span>
                  </div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Audition Offer</div>
                  <div style={styles.detailValue}>{selectedApplicant.audition_offer === 'yes' ? '✅ Yes' : '❌ No'}</div>
                </div>
                <div>
                  <div style={styles.detailLabel}>Submitted</div>
                  <div style={styles.detailValue}>{new Date(selectedApplicant.created_at).toLocaleString()}</div>
                </div>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button onClick={handleCloseDetail} style={{ ...styles.btnSecondary, marginLeft: 0 }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
